/** Constantes métier ISBD (référence fonctionnelle, code neuf). */

/** Seuil de viabilité : somme des 5 études de faisabilité. */
export const FEASIBILITY_VIABILITY_THRESHOLD = 292;

/** Quota CDC : projets actifs créés par abonné et par année civile. */
export const MAX_PROJECTS_PER_YEAR = 12;

/** Durée d’archive avant purge définitive (mois). */
export const PROJECT_ARCHIVE_MONTHS = 6;

/** Nombre minimum d’idées proposées en idéation assistée. */
export const MIN_ASSISTED_IDEA_SUGGESTIONS = 3;

export const FEASIBILITY_TYPES = [
  'technical',
  'legal',
  'financial',
  'commercial',
  'organizational',
] as const;

export type FeasibilityType = (typeof FEASIBILITY_TYPES)[number];

export const FEASIBILITY_LABELS: Record<FeasibilityType, string> = {
  technical: 'Technique',
  legal: 'Juridique',
  financial: 'Financière',
  commercial: 'Commerciale',
  organizational: 'Organisationnelle',
};

export const PROJECT_PROGRESS_STEPS = [
  { key: 'feasibility', label: 'Faisabilité finalisée' },
  { key: 'business_plan', label: 'Business plan' },
  { key: 'business_model_canvas', label: 'Business model canvas' },
  { key: 'risks', label: 'Analyse des risques' },
  { key: 'preparation', label: 'Plan de préparation' },
  { key: 'deployment', label: 'Plan de déploiement' },
  { key: 'steering', label: 'Pilotage stratégique' },
  { key: 'maturation', label: 'Parcours de maturation' },
] as const;

export const PREPARATION_PLAN_AXES = [
  {
    order: 1,
    axe: '1. Préparation personnelle et entrepreneuriale',
    objectif: "Renforcer les compétences et la posture de l'entrepreneur.",
  },
  {
    order: 2,
    axe: '2. Validation terrain du projet',
    objectif: "Confirmer la viabilité et l'adaptabilité du produit ou service.",
  },
  {
    order: 3,
    axe: '3. Préparation opérationnelle',
    objectif: 'Organiser les moyens nécessaires au démarrage.',
  },
  {
    order: 4,
    axe: '4. Préparation financière et juridique',
    objectif: 'Sécuriser les ressources et le cadre légal.',
  },
  {
    order: 5,
    axe: '5. Préparation commerciale et marketing',
    objectif: "Préparer l'acquisition des premiers clients.",
  },
  {
    order: 6,
    axe: '6. Préparation au pilotage et à la croissance',
    objectif: "Mettre en place les outils de gestion et d'amélioration continue.",
  },
] as const;

export const DEPLOYMENT_PLAN_AXES = [
  {
    order: 1,
    axe: '1. Lancement des activités',
    objectif: "Démarrer officiellement l'entreprise.",
  },
  {
    order: 2,
    axe: '2. Déploiement commercial',
    objectif: 'Acquérir les premiers clients et générer les premières ventes.',
  },
  {
    order: 3,
    axe: '3. Déploiement opérationnel',
    objectif: 'Assurer une exécution efficace des activités.',
  },
  {
    order: 4,
    axe: '4. Pilotage de la performance',
    objectif: 'Suivre les résultats et prendre les bonnes décisions.',
  },
  {
    order: 5,
    axe: '5. Gestion des risques et amélioration continue',
    objectif: "Sécuriser le développement de l'entreprise.",
  },
  {
    order: 6,
    axe: '6. Développement et croissance',
    objectif: 'Anticiper sur les stratégies de maximisation de la rentabilité et de la croissance.',
  },
] as const;

export const PLAN_ROW_STATUSES = [
  { value: 'non_debuté', label: 'Non débuté' },
  { value: 'en_cours', label: 'En cours' },
  { value: 'realise', label: 'Réalisé' },
] as const;

export type PlanRowStatus = (typeof PLAN_ROW_STATUSES)[number]['value'];

export interface StrategicPlanRow {
  order: number;
  axe: string;
  objectif: string;
  actions: string;
  responsable: string;
  echeance: string;
  statut: PlanRowStatus | '';
}

/** @deprecated Conservé pour compat lecture anciennes données — préférer PREPARATION_PLAN_AXES */
export const PREPARATION_DEFAULT_ACTIVITIES = PREPARATION_PLAN_AXES.map((a) => ({
  order: a.order,
  name: a.axe,
  tasks: [{ label: '', dueDate: '', owner: '', status: '' }],
}));

/** @deprecated Conservé pour compat lecture anciennes données — préférer DEPLOYMENT_PLAN_AXES */
export const DEPLOYMENT_DEFAULT_ACTIVITIES = DEPLOYMENT_PLAN_AXES.map((a) => ({
  order: a.order,
  name: a.axe,
  tasks: [{ label: '', dueDate: '', owner: '', status: '' }],
}));

export function emptyStrategicPlanRows(
  kind: 'prep' | 'deploy',
): StrategicPlanRow[] {
  const axes = kind === 'prep' ? PREPARATION_PLAN_AXES : DEPLOYMENT_PLAN_AXES;
  return axes.map((a) => ({
    order: a.order,
    axe: a.axe,
    objectif: a.objectif,
    actions: '',
    responsable: '',
    echeance: '',
    statut: '' as const,
  }));
}

export function normalizeStrategicPlanRows(
  kind: 'prep' | 'deploy',
  raw: unknown,
): StrategicPlanRow[] {
  const base = emptyStrategicPlanRows(kind);
  if (!Array.isArray(raw) || !raw.length) return base;

  // Ancien format activities[{order,name,tasks}]
  if (raw[0] && typeof raw[0] === 'object' && 'tasks' in (raw[0] as object)) {
    const byOrder = new Map<number, { name?: string; tasks?: { label?: string; dueDate?: string; owner?: string; status?: string }[] }>();
    for (const item of raw as { order?: number; name?: string; tasks?: { label?: string; dueDate?: string; owner?: string; status?: string }[] }[]) {
      if (item.order != null) byOrder.set(item.order, item);
    }
    return base.map((row) => {
      const old = byOrder.get(row.order);
      const t = old?.tasks?.[0];
      const statusMap: Record<string, PlanRowStatus | ''> = {
        todo: 'non_debuté',
        doing: 'en_cours',
        done: 'realise',
        blocked: 'en_cours',
        non_debuté: 'non_debuté',
        en_cours: 'en_cours',
        realise: 'realise',
      };
      return {
        ...row,
        actions: t?.label ?? '',
        responsable: t?.owner ?? '',
        echeance: t?.dueDate ?? '',
        statut: statusMap[t?.status ?? ''] ?? '',
      };
    });
  }

  // Nouveau format StrategicPlanRow[]
  const byOrder = new Map<number, Partial<StrategicPlanRow>>();
  for (const item of raw as Partial<StrategicPlanRow>[]) {
    if (item.order != null) byOrder.set(item.order, item);
  }
  return base.map((row) => {
    const saved = byOrder.get(row.order);
    if (!saved) return row;
    return {
      ...row,
      // axe & objectif toujours issus du CDC (non modifiables)
      actions: saved.actions ?? '',
      responsable: saved.responsable ?? '',
      echeance: saved.echeance ?? '',
      statut: (saved.statut as PlanRowStatus | '') ?? '',
    };
  });
}

export const STRATEGIC_AXES = [
  {
    code: 'axe1',
    title: 'Performance et excellence opérationnelle',
    order: 1,
  },
  { code: 'axe2', title: 'Valeur client et impact marché', order: 2 },
  { code: 'axe3', title: 'Innovation et transformation', order: 3 },
  {
    code: 'axe4',
    title: 'Gouvernance, durabilité et résilience',
    order: 4,
  },
] as const;

export const CONTEST_MATURATION_STEPS = [
  { slug: 'initial_review', label: 'Examen des conditions initiales', order: 1 },
  { slug: 'capacity_analysis', label: 'Examen du besoin adressé et solution proposée', order: 2 },
  { slug: 'feasibility', label: "Examen des dossiers d'études/diagnostic et business model/plan", order: 3 },
  { slug: 'business_plan', label: 'Examen de la capacité entrepreneuriale', order: 4 },
  { slug: 'presentation', label: 'Présentation du projet', order: 5 },
  { slug: 'field_test', label: 'Validation terrain/expérimentation', order: 6 },
] as const;

/** Catégories de la base documentaire concours (docs de référence). */
export const CONTEST_RESOURCE_CATEGORIES = [
  { key: 'reglement', label: 'Règlement' },
  { key: 'guide', label: 'Guide' },
  { key: 'modele', label: 'Modèle / canevas' },
  { key: 'faq', label: 'FAQ' },
  { key: 'general', label: 'Général' },
] as const;

export const BMC_BLOCKS = [
  { key: 'value_propositions', label: 'Proposition de valeur' },
  { key: 'customer_segments', label: 'Segments de clientèle' },
  { key: 'channels', label: 'Canaux' },
  { key: 'customer_relationships', label: 'Relations clients' },
  { key: 'revenue_streams', label: 'Flux de revenus' },
  { key: 'key_resources', label: 'Ressources clés' },
  { key: 'key_activities', label: 'Activités clés' },
  { key: 'key_partners', label: 'Partenaires clés' },
  { key: 'cost_structure', label: 'Structure de coûts' },
] as const;

export function viabilityFromScore(score: number): 'viable' | 'not_viable' {
  return score >= FEASIBILITY_VIABILITY_THRESHOLD ? 'viable' : 'not_viable';
}

export function feasibilityViabilityLabel(viability: 'viable' | 'not_viable'): string {
  return viability === 'viable' ? 'Projet viable' : 'Projet non viable';
}

export const RESOURCE_STATUS_LABELS: Record<string, string> = {
  disponible: 'Disponible',
  a_recruter: 'À recruter / acquérir',
  indisponible: 'Indisponible',
};

export function resourceStatusLabel(status: string): string {
  return RESOURCE_STATUS_LABELS[status] ?? status;
}
